import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Custom hook for managing subdivision widths and resize observers
 * @returns {Object} - Object with subdivision state and handlers
 */
export function useSubdivisionWidths() {
  const [subdivisionWidths, setSubdivisionWidths] = useState({});
  const subdivisionRefs = useRef({});
  const resizeObservers = useRef({});

  const captureSubdivisionWidths = useCallback((colIndex, specialistsElement, faoElement) => {
    if (specialistsElement && faoElement) {
      const measureWidths = () => {
        const specialistsWidth = specialistsElement.offsetWidth;
        const faoWidth = faoElement.offsetWidth;
        
        setSubdivisionWidths(prev => ({
          ...prev,
          [colIndex]: {
            specialists: specialistsWidth,
            fao: faoWidth
          }
        }));
      };

      measureWidths();

      const resizeObserver = new ResizeObserver(() => {
        measureWidths();
      });

      resizeObserver.observe(specialistsElement);
      resizeObserver.observe(faoElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  const resetSubdivisionWidths = useCallback(() => {
    Object.values(resizeObservers.current).forEach(cleanup => {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });
    resizeObservers.current = {};
    setSubdivisionWidths({});
  }, []);

  useEffect(() => {
    return () => {
      Object.values(resizeObservers.current).forEach(cleanup => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, []);

  return {
    subdivisionWidths,
    subdivisionRefs,
    resizeObservers,
    captureSubdivisionWidths,
    resetSubdivisionWidths
  };
}
